import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Boxes, ListChecks, Link2 } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { FaqSection } from "@/components/seo/FaqSection";
import { absoluteUrl, breadcrumbJsonLd, ldScript, pageHead } from "@/lib/seo";

const PATH = "/gs1-inventory";
const TITLE = "GS1 & Inventory Tools — Digital Link, Sunrise 2027";
const DESC =
  "Decode GS1 Digital Link URLs, run a barcode-driven inventory list with CSV export, clean bulk barcode lists, and read up on the Sunrise 2027 transition.";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "GS1 & Inventory", path: PATH },
];

type ToolCard = {
  path: string;
  label: string;
  tagline: string;
  description: string;
  icon: typeof Link2;
  formats: string[];
};

const TOOLS: ToolCard[] = [
  {
    path: "/gs1-digital-link-decoder",
    label: "GS1 Digital Link Decoder",
    tagline: "Parse Sunrise-2027 URLs",
    description:
      "Paste a GS1 Digital Link URL (the new web-friendly form of a GS1 payload) and get a breakdown of every Application Identifier — GTIN, batch, expiry, serial, weight and the rest — in plain English.",
    icon: Link2,
    formats: ["Digital Link", "AIs (01, 10, 17, 21…)", "GTIN"],
  },
  {
    path: "/inventory-scanner",
    label: "Inventory Scanner",
    tagline: "Camera-driven stock list",
    description:
      "A lightweight inventory list backed by the device camera — scan UPC, EAN, GTIN or Data Matrix codes, edit counts, and export the whole list to CSV. No accounts, no upload.",
    icon: Boxes,
    formats: ["Camera scan", "Manual edit", "CSV export"],
  },
  {
    path: "/batch-barcode-scanner",
    label: "Batch Barcode Tools",
    tagline: "Bulk validate & clean",
    description:
      "Paste a list of barcode values and validate, deduplicate and normalise them in one pass. Outputs a flagged report and a clean CSV ready for import into a wholesaler or PIM feed.",
    icon: ListChecks,
    formats: ["Bulk validate", "Dedupe", "CSV in / out"],
  },
];

/** Sunrise 2027 is a content / reference piece rather than an interactive
 *  tool, so it sits in its own row below the tool grid — visually
 *  distinguished from the camera-driven and bulk-validating utilities above. */
type RelatedGuide = { path: string; label: string; description: string };
const RELATED_GUIDES: RelatedGuide[] = [
  {
    path: "/barcode-guides/sunrise-2027-explained",
    label: "Sunrise 2027 explained",
    description:
      "The retail POS transition that motivates every GS1 Digital Link on a consumer pack. Plain-English walkthrough of what brands, retailers and packs need to do, plus the dual-carrier interim.",
  },
  {
    path: "/barcode-guides/gs1-digital-link",
    label: "GS1 Digital Link explained",
    description:
      "The URL grammar that lets one 2D barcode carry the retail GTIN for the till and a working URL for the phone — anatomy, path vs query AIs, and resolver basics.",
  },
];

const FAQ = [
  {
    q: "What is GS1 Sunrise 2027?",
    a: "GS1 Sunrise 2027 is the global commitment that retail point-of-sale scanners will accept 2D codes (QR Code with a GS1 Digital Link, or GS1 Data Matrix) alongside the traditional 1D UPC / EAN by the end of 2027. It lets a single symbol on a consumer pack carry GTIN plus batch, expiry, serial and more — usable both at the checkout and by the shopper's phone. See the Sunrise 2027 Guide for the full picture.",
  },
  {
    q: "Do I Need to Switch From 1D UPC / EAN to 2D Right Now?",
    a: "No — Sunrise 2027 is additive. POS systems must accept 2D by end of 2027, but the existing 1D UPC / EAN remains valid through and past that date. Most brands will dual-print (1D barcode + GS1 Digital Link QR) during the transition. The Sunrise 2027 Guide walks through the practical timeline.",
  },
  {
    q: "What Is a GS1 Digital Link?",
    a: "A GS1 Digital Link is a web URL that encodes GTIN plus optional Application Identifiers — `https://id.example.com/01/09506000134352/10/ABC123` is a worked example. It is scannable by a phone (becomes a URL) and by a POS scanner (the scanner extracts the GTIN from the URL and the till looks up the price as usual), so a single 2D code replaces the 1D barcode + the marketing QR.",
  },
  {
    q: "Where Does Inventory Data Go When I Use the Inventory Scanner?",
    a: "It stays in your browser. The Inventory Scanner persists rows to local storage so you can come back to your list later, and gives you a CSV export whenever you want to move the data elsewhere. There is no account, no server-side database, and no telemetry on the scanned values.",
  },
  {
    q: "Why a Batch Tool When You Already Have Single-Value Validators?",
    a: "When you're prepping a supplier feed, audit list or product import, you have hundreds or thousands of barcodes — pasting them one at a time isn't realistic. The Batch Barcode Tools page accepts a whole list, runs each value through the GTIN validator, dedupes, and outputs a flagged CSV in one pass.",
  },
  {
    q: "How Long Is a GS1 Barcode?",
    a: "It depends on the symbol. A GTIN is 8, 12, 13 or 14 digits long and travels in an EAN-8, UPC-A, EAN-13, ITF-14 or GS1-128 barcode, while a GS1-128 holding several AIs can carry up to 48 data characters. Physically, an EAN-13 at 100% magnification is 37.29 mm wide including its quiet zones, and retail symbols may be printed from 80% to 200% of that size.",
  },
  {
    q: "How Many GS1 Barcodes Do I Need?",
    a: "You need one GTIN for every distinct trade item: each size, flavor, color or pack count gets its own number. A case or other grouping that is ordered or shipped as a unit needs its own GTIN as well, separate from the units inside it. Count your variants and pack levels before licensing a GS1 Company Prefix, because the prefix length sets how many GTINs you can create.",
  },
  {
    q: "Can I decode a GS1 Data Matrix here?",
    a: "Yes — for plain decoding use the Data Matrix Reader. For Digital Link URLs encoded in any symbology, the Digital Link Decoder parses the URL form and surfaces every AI. To generate a Digital Link as either QR or Data Matrix, use the GS1 Digital Link Generator.",
  },
];

export const Route = createFileRoute("/gs1-inventory")({
  component: Gs1InventoryPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
      ogType: "website",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          description: DESC,
          url: absoluteUrl(PATH),
          hasPart: TOOLS.map((t) => ({
            "@type": "SoftwareApplication",
            name: t.label,
            description: t.description,
            url: absoluteUrl(t.path),
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Any (Web)",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          })),
        }),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function Gs1InventoryPage() {
  return (
    <ToolLayout
      eyebrow="Hub"
      title="GS1 & Inventory Tools"
      description="Decode GS1 Digital Link URLs into structured Application Identifiers, run a camera-driven inventory list, clean bulk barcode feeds and read up on the GS1 Sunrise 2027 transition to 2D barcodes. The decoder also reads bracketed element strings and raw scanner payloads, and turns six-digit expiry and production dates into calendar dates."
      crumbs={CRUMBS}
      content={
        <>
          <ToolSeoSections
            aboutHeading="About the GS1 & Inventory Tools"
            about={
              <>
                <p>
                  GS1 is the standards body behind the world&apos;s product identifiers — GTIN (UPC,
                  EAN), GLN (locations), SSCC (logistic units) — and the data-carrier formats that
                  encode them (Code 128 / GS1-128, GS1 Data Matrix, GS1 Digital Link). This hub groups
                  the ReadBarcode tools that work at the GS1 / inventory level rather than at the
                  single-barcode level: Digital Link parsing, the Sunrise 2027 reference, and the
                  camera- and bulk-driven inventory utilities.
                </p>
                <p>
                  Need to build a Digital Link rather than parse one? See the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link>
                  . Need to validate the GTIN inside a Digital Link? Pair with the <Link to="/gtin-validator">GTIN Validator</Link> on the lookup hub.
                </p>
              </>
            }
            whenToUse={[
              "Decoding a GS1 Digital Link URL that came off a packaging proof or a 2D code on a consumer pack.",
              "Planning a Sunrise-2027-ready label that prints a 1D UPC / EAN alongside a GS1 Digital Link QR.",
              "Running a stock-take from your phone: scan the shelf, edit counts, export the list to your spreadsheet.",
              "Cleaning a supplier-provided barcode list before importing it into a PIM or wholesaler portal.",
              "Auditing GS1 application-identifier usage across a batch of cartons before they ship.",
              "Cross-referencing a GS1 Data Matrix payload against a product master record by AI.",
            ]}
            howItWorks={[
              {
                title: "Pick the right tool",
                desc: "Digital Link Decoder for single URLs, Inventory Scanner for live stock-takes, Batch Tools for bulk feeds, Sunrise 2027 Guide for the 'why'.",
              },
              {
                title: "Paste, scan or upload",
                desc: "All inputs are local — paste a URL, scan with the camera, or drag a CSV. Nothing is uploaded.",
              },
              {
                title: "See the structured data",
                desc: "Tools surface the GTIN, AIs, validation status and any flagged anomalies. Digital Link AIs are mapped to their human-readable meanings.",
              },
              {
                title: "Export or hand off",
                desc: "CSV export on the inventory and batch tools, copy-to-clipboard on the decoder, downloadable label artwork from the generators.",
              },
              {
                title: "Round-trip-verify",
                desc: "Generate a Digital Link, decode it back here, validate the underlying GTIN — same workflow you'll need for your own QA gate.",
              },
            ]}
            whatToAvoid={[
              "Don't confuse a GS1 Digital Link with a plain URL QR — the AI structure matters for resolver behaviour, and the decoder will flag malformed AIs.",
              "Don't ship cartons with batch / expiry encoded only in human-readable text — encode them as GS1 AIs (10 / 17) so scanners can pick them up automatically.",
              "Don't rely on the Inventory Scanner as a system of record — it's a quick local list. Export to CSV and import into your ERP / WMS for anything you need to audit.",
              "Don't paste personally-identifying data into the Batch tool by mistake — it's barcode-only and won't validate as anything if you do, but it's still worth checking your inputs.",
              "Don't assume every 2D code on a pack is a Digital Link — some are still marketing QRs that point to a campaign URL with no GS1 AIs at all.",
            ]}
            tips={[
              "If your Inventory Scanner list grows large, export to CSV every session and clear the list — local storage is generous but not unlimited.",
              "The Digital Link Decoder accepts both the canonical `id.gs1.org` form and brand-resolver URLs (your own domain); both work as long as the path segments follow the GS1 grammar.",
              "Use the Batch tool's CSV-in flow to validate a whole vendor onboarding list — flagged rows are usually the ones the vendor mis-typed in their spreadsheet.",
              "For Sunrise 2027 planning, pair the Digital Link Generator with the Wi-Fi / vCard QR generators when drafting promo cards — the Print Setup dialog will tile any of them onto Avery sheets.",
              "When auditing AI usage, copy the decoded breakdown into a markdown table — the human-readable AI names make it readable for non-GS1 colleagues without further explanation.",
            ]}
          />
          <h2>GS1 Barcode Decoder Online</h2>
          <p>A GS1 barcode decoder splits one scanned string into its Application Identifiers, so the GTIN, batch, expiry date and serial number appear as separate fields. On this hub that job belongs to the <Link to="/gs1-digital-link-decoder">GS1 Digital Link decoder</Link>, a GS1 decoder online that runs in your browser.</p>
          <p>It accepts three input shapes and detects which one you pasted: a Digital Link URL, an element string with the AI numbers in parentheses, or a raw payload without them. The <Link to="/barcode-guides/gs1-digital-link">GS1 Digital Link guide</Link> explains how the URL form places AIs in the path and the query string.</p>
          <ol>
            <li>Read the code with a camera or image reader, or copy the text printed under the bars.</li>
            <li>Paste the URL, element string or raw payload into the decoder.</li>
            <li>Compare each decoded field with the pack, and copy the GTIN into the GTIN Validator if its check digit is in doubt.</li>
          </ol>
          <p>The decoder checks structure, not ownership. A GS1 barcode decode tells you which AIs are present and flags ones it does not recognize, but it cannot confirm that a GTIN is licensed to the brand on the pack.</p>
          <h3>GS1-128 Barcode Decoder</h3>
          <p>A GS1-128 barcode is a Code 128 symbol that begins with the FNC1 character, which tells the scanner the data is a chain of GS1 Application Identifiers. Shipping labels use it for the SSCC (AI 00) of a pallet and for the GTIN, batch and dates of a case. A single GS1-128 symbol holds at most 48 data characters.</p>
          <p>{`To decode one, type the human-readable line under the bars with its AIs in parentheses, for example (01)09506000134352(17)271231(10)LOT42. In raw scanner output, a variable-length field such as batch (10) or serial (21) is closed by the Group Separator character when another AI follows; type it as ^ or {GS}.`}</p>
          <h3>Reading a GS1 Barcode Expiry Date</h3>
          <p>The expiry date sits in AI (17) as six digits in year, month, day order, so 271231 means December 31, 2027. The same GS1 barcode date format applies to the production date (11), packaging date (13) and best-before date (15).</p>
          <p>A day of 00 means the code gives only a year and month, and the decoder shows that day as a dash. In a Digital Link URL the expiry date travels in the query string, as in ?17=271231. The <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 guide</Link> explains why retailers want these dates in a 2D code that the till can read.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={
        <>
          <section aria-label="Related guides" className="mt-12">
            <div className="flex items-baseline justify-between gap-4">
              <h2 className="text-xl font-semibold tracking-tight md:text-2xl">Related Guides</h2>
              <Link
                to="/barcode-guides"
                className="text-sm font-medium text-primary hover:underline underline-offset-4"
              >
                All guides →
              </Link>
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Plain-English explainers that sit alongside the GS1 toolset above — start here if
              you&apos;re planning packaging artwork or deciding what Sunrise-2027 readiness actually
              means for your brand.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {RELATED_GUIDES.map((g) => (
                <Link
                  key={g.path}
                  to={g.path}
                  className="group flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Guide
                    </div>
                    <div className="mt-1 text-sm font-semibold">{g.label}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{g.description}</p>
                  </div>
                  <ArrowRight
                    className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                    aria-hidden
                  />
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-xl font-semibold tracking-tight md:text-2xl">
              Related Tools and Guides
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                {
                  to: "/gs1-digital-link-generator",
                  label: "GS1 Digital Link Generator",
                  desc: "Compose a Digital Link URL from a GTIN plus AIs and render it as QR or Data Matrix.",
                },
                {
                  to: "/data-matrix-generator",
                  label: "Data Matrix Generator",
                  desc: "Encode GS1 payloads (UDI, batch, expiry) as a Data Matrix barcode.",
                },
                {
                  to: "/lookup-validators",
                  label: "Lookup & Validator Tools",
                  desc: "Validate the GTIN inside a Digital Link, calculate check digits, identify unknown formats.",
                },
                {
                  to: "/data-matrix-reader",
                  label: "Data Matrix Reader",
                  desc: "Decode a plain or GS1 Data Matrix from camera or image input.",
                },
                {
                  to: "/barcode-guides/what-is-a-gtin",
                  label: "What is a GTIN?",
                  desc: "Plain-English guide to the GTIN identifier that sits inside every Digital Link.",
                },
                {
                  to: "/barcode-guides/1d-vs-2d-barcodes",
                  label: "1D vs 2D barcodes",
                  desc: "When 1D UPC / EAN is enough and when a 2D Digital Link earns its place.",
                },
              ].map((t) => (
                <Link
                  key={t.to}
                  to={t.to}
                  className="group flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-4 transition hover:border-primary/50 hover:bg-muted/40"
                >
                  <div>
                    <div className="text-sm font-semibold">{t.label}</div>
                    <p className="mt-1 text-sm text-muted-foreground">{t.desc}</p>
                  </div>
                  <ArrowRight
                    className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary"
                    aria-hidden
                  />
                </Link>
              ))}
            </div>
          </section>
        </>
      }
    >
      <section aria-label="GS1 and inventory tools">
        <div className="grid gap-4 sm:grid-cols-2">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.path}
                to={t.path}
                className="group flex h-full flex-col rounded-lg border border-border bg-background p-5 transition hover:border-primary/50 hover:bg-muted/30"
              >
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  {t.tagline}
                </div>
                <h2 className="mt-3 text-lg font-semibold leading-snug group-hover:text-primary">
                  {t.label}
                </h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{t.description}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {t.formats.map((f) => (
                    <span
                      key={f}
                      className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
                    >
                      {f}
                    </span>
                  ))}
                </div>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                  Open tool
                  <ArrowRight
                    className="h-3.5 w-3.5 transition group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </ToolLayout>
  );
}
